import {IItemContent} from "@connox/typescript-library";
import React, {Fragment, useEffect} from "react";
import RatingStars from "../lib/ratingStars/RatingStars";
import AddToWishlistButton from "../wishlist/AddToWishlistButton/AddToWishlistButton";
import styles from "./ItemCard.module.scss";
import {useTranslation} from "react-i18next";
import EnergyLabel from "../energyLabel/EnergyLabel";
import {BrowserStorage} from "../../storage/BrowserStorage";
import Image from "./Image";

type IItemCardProps = {
  item: IItemContent;
  onClick?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  swipeable?: boolean;
  scrollInfo?: ScrollInfo | null;
};

export type ScrollInfo = {
  itemId: number | null;
  position: number;
};

export const scrollInfoStorage = new BrowserStorage<ScrollInfo>("sessionStorage", "ScrollInfo", false);

const ItemCard: React.FC<IItemCardProps> = ({item, onClick, swipeable = false, scrollInfo}) => {
  const {attributes, daysSincePublishing, images, itemRank, prices, ratingAverage, title, url, itemId} = item;
  const energyEfficiency = attributes.find((attrib) => attrib.itemAttributeId === 100361);
  const energyLabel = images.find((image) => !!image.energyLabel)?.energyLabel || undefined;

  // try to find a price with the quantity of 1, else take the only one in the array
  const {advertise, discountPercentage, price, quantity, retail} =
    prices.find((prc) => prc.quantity.from === 1) || prices[0];
  const {t} = useTranslation();

  // this effect will restore the last scroll position in case scrollinfo prop is passed.
  useEffect(() => {
    if (scrollInfo) {
      const {itemId: storedItemId, position} = scrollInfo;
      if (storedItemId === itemId) {
        if (typeof window.scrollTo === "function") {
          window.scrollTo({left: 0, top: position});
        } else {
          window.scroll(0, position);
        }
        scrollInfoStorage.remove(document.body.id);
      }
    }
  }, []);

  const Badge: React.FC = () => {
    if (discountPercentage && discountPercentage > 5) {
      return <span className={styles.saleBadge}>{`-${discountPercentage}`}</span>;
    } else if (itemRank && itemRank < 300) {
      return <span className={styles.topBadge}>TOP</span>;
    } else if (daysSincePublishing && daysSincePublishing < 30) {
      const newText = document.body.dataset.lang === "de" ? "NEU" : "NEW";

      return <span className={styles.newBadge}>{newText}</span>;
    } else {
      return null;
    }
  };

  return (
    <a
      className={`${styles.itemWrapper} ${swipeable ? styles.swipeable : ""}`}
      href={url}
      title={t("{{title}} aufrufen", {title})}
      onClick={onClick}
    >
      <div className={styles.imgPlaceholder}>
        <Image item={item} />
      </div>
      <Badge />
      <div className={styles.description}>{title}</div>
      {!!energyEfficiency && (
        <EnergyLabel energyClass={energyEfficiency.value ?? "G"} size={"small"} media={energyLabel} />
      )}
      <div className={styles.priceVoted}>
        <span className={styles.price}>
          {quantity.from > 1 && <span>{t("Ab")}&nbsp;</span>}
          {advertise && (
            <Fragment>
              <s>{t("UVP\u00A0{{price}}", {price: retail.formatted})}</s>{" "}
            </Fragment>
          )}
          <span className={advertise ? "red" : undefined}>{price.formatted}</span>&nbsp;*
        </span>
        {!!ratingAverage.count && <RatingStars metaProp={false} rating={ratingAverage.average / 10} size="small" />}
      </div>
      <AddToWishlistButton itemId={itemId} animated={true} emptyColor={"#a4a4a4"} size={23} />
    </a>
  );
};

export default ItemCard;
